import { isNil, isString, type Nil } from "@mcwd/typescript-type-guards";

export type HTMLElementFindFn<Element extends HTMLElement> = (parent: ParentNode) => (Element | Nil);


function WaitForElementWithSelectorFn<Element extends HTMLElement = HTMLElement>(
  selectorFn:  HTMLElementFindFn<Element>,
  parentElement: ParentNode = document,
  maxRetries: number,
  pollIntervalInMs: number
): Promise<Element> {
  return new Promise<Element>((resolve, reject) => {
    if (maxRetries < 0) {
      reject(new Error("WaitForElementAsPromise: maxRetries is less than zero!"));
    }
    if (pollIntervalInMs < 0) {
      reject(new Error("WaitForElementAsPromise: pollIntervalInMs is less than zero!"));
    }
    let retries = 0;
    const waitForEl = () => {
      const el = selectorFn(parentElement);
      if (! isNil(el)) {
        resolve(el);
      }
      if (retries < maxRetries) {
        retries++;
        setTimeout(waitForEl, pollIntervalInMs);
      }
      else {
        reject(new Error("WaitForElementAsPromise: Element not found!"));
      }
    };
    waitForEl();
  });
}

export async function WaitForElementAsPromise<Element extends HTMLElement = HTMLElement>(
  elementSelector: string | HTMLElementFindFn<Element>,
  parentElement: ParentNode = document,
  maxRetries?: number,
  pollIntervalInMs?: number
): Promise<Element> {
  maxRetries ??= 10;
  pollIntervalInMs ??= 500;
  if (isString(elementSelector)) {
    const selector = elementSelector;
    elementSelector = (parent: ParentNode) => parent.querySelector<Element>(selector);
  }
  return await WaitForElementWithSelectorFn(elementSelector, parentElement, maxRetries, pollIntervalInMs);
}
